import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  FolderIcon,
  UsersIcon,
  RectangleStackIcon,
} from "@heroicons/react/24/outline";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { Project } from "@/services/api";
import { cn } from "@/lib/utils";
import { ContentBreadcrumbs } from "@/features/shell/BreadcrumbBar";
import { ShellSidebarSlot } from "@/contexts/ShellSidebarContext";

export interface ProjectsLayoutProps {
  children: React.ReactNode;
  /** @deprecated Use NavbarToolbarContext; toolbar is now in navbar. Kept for backwards compatibility - ignored. */
  topBar?: React.ReactNode;
  /** Current list of personal projects. */
  projects: Project[];
  loading?: boolean;
  onRefresh: () => void;
  onCreateOpen: () => void;
  onEdit: (project: Project) => void;
  onDelete: (project: Project) => void;
  /** Shared projects (placeholder until backend). */
  sharedProjects?: Project[];
  /** Optional right sidebar content (e.g. document details). Rendered at same level as nav rail and content. */
  rightSidebar?: React.ReactNode;
  /**
   * Optional handler invoked when a resource item is dropped onto a project
   * entry in the sidebar.
   */
  onItemDrop?: (
    projectId: string,
    payload: { id: string; kind: 'Document' | 'Chat' | 'PDF' }[]
  ) => void;
}

export default function ProjectsLayout({
  children,
  topBar,
  projects,
  loading = false,
  onRefresh,
  onCreateOpen,
  onEdit,
  onDelete,
  sharedProjects = [],
  rightSidebar,
  onItemDrop,
}: ProjectsLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const [dragOverProjectId, setDragOverProjectId] = useState<string | null>(null);

  const pathId = location.pathname.match(/^\/projects\/([a-f0-9-]+)$/)?.[1];
  const selectedId = pathId || null;
  const isAllSelected = location.pathname === "/projects" && !pathId;

  const handleSelectAll = () => {
    navigate("/projects");
  };

  const handleSelectProject = (id: string) => {
    navigate(`/projects/${id}`);
  };

  return (
    <div className={cn(
      "app-frame app-frame--shell-layout app-frame--with-projects-sidebar h-full flex flex-col",
      rightSidebar != null && "app-frame--with-document-sidebar"
    )}>
      <div className="app-frame-inner flex flex-1 min-h-0">
      <ShellSidebarSlot>
        <ScrollArea className="flex-1 min-w-0 min-h-0">
          <nav className="p-2 min-w-0 max-w-full overflow-hidden">
              {/* Personal Projects */}
              <div className="mb-6">
                <div className="flex items-center justify-between px-3 py-2 mb-2">
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Personal
                  </h3>
                  <Badge variant="secondary" className="text-xs">
                    {loading ? "..." : projects.length}
                  </Badge>
                </div>
                <div className="space-y-1 min-w-0">
                  {/* All Resources as first item */}
                  <button
                    onClick={handleSelectAll}
                    className={cn(
                      "w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-left group",
                      isAllSelected
                        ? "bg-[hsl(var(--primary)/0.1)] text-primary"
                        : "hover:bg-accent"
                    )}
                  >
                    <div className={cn(
                      "p-1 rounded-md flex-shrink-0 transition-colors",
                      isAllSelected
                        ? "bg-muted"
                        : "bg-muted group-hover:bg-accent-foreground/10"
                    )}>
                      <RectangleStackIcon className="h-3.5 w-3.5" />
                    </div>
                    <span className={cn(
                      "text-sm",
                      isAllSelected ? "font-semibold" : "font-medium"
                    )}>
                      All Resources
                    </span>
                  </button>

                  {projects.length === 0 && !loading ? (
                    <div className="px-3 py-8 text-center">
                      <div className="rounded-lg border border-dashed border-border bg-muted/30 p-6">
                        <FolderIcon className="h-10 w-10 mx-auto mb-3 text-muted-foreground/60" />
                        <p className="text-sm font-medium text-foreground mb-1">
                          No projects yet
                        </p>
                        <p className="text-xs text-muted-foreground mb-4">
                          Create your first project to organize your documents
                        </p>
                        <Button
                          onClick={onCreateOpen}
                          size="sm"
                          className="h-7 text-xs"
                        >
                          <PlusIcon className="h-3.5 w-3.5 mr-1.5" />
                          Create Project
                        </Button>
                      </div>
                    </div>
                  ) : (
                    projects.map((project) => {
                      const selected = selectedId === project.id;
                      const isDragOver = dragOverProjectId === project.id;
                      return (
                        <div
                          key={project.id}
                          className={cn(
                            "flex items-center gap-2 px-3 py-2 rounded-lg transition-all group min-w-0 overflow-hidden border border-transparent",
                            selected
                              ? "bg-[hsl(var(--primary)/0.1)] text-primary"
                              : "hover:bg-accent",
                            isDragOver && !selected && "border-primary/50 bg-accent/60"
                          )}
                          onDragOver={(e) => {
                            if (!onItemDrop) return;
                            if (e.dataTransfer.types.includes('application/x-rcm-project-item')) {
                              e.preventDefault();
                              e.dataTransfer.dropEffect = 'copy';
                            }
                          }}
                          onDragEnter={(e) => {
                            if (!onItemDrop) return;
                            if (!e.dataTransfer.types.includes('application/x-rcm-project-item')) {
                              return;
                            }
                            setDragOverProjectId(project.id);
                            if (typeof document !== 'undefined') {
                              const label = document.getElementById('rcm-drag-preview-label');
                              if (label) {
                                label.textContent = `Add to "${project.name}"`;
                              }
                            }
                          }}
                          onDragLeave={(e) => {
                            if (!onItemDrop) return;
                            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                              setDragOverProjectId((prev) =>
                                prev === project.id ? null : prev
                              );
                            }
                          }}
                          onDrop={(e) => {
                            if (!onItemDrop) return;
                            if (!e.dataTransfer.types.includes('application/x-rcm-project-item')) {
                              return;
                            }
                            e.preventDefault();
                            setDragOverProjectId(null);
                            const raw = e.dataTransfer.getData('application/x-rcm-project-item');
                            if (!raw) return;
                            try {
                              const payload = JSON.parse(raw) as {
                                id: string;
                                kind: 'Document' | 'Chat' | 'PDF';
                              }[];
                              if (Array.isArray(payload) && payload.length > 0) {
                                onItemDrop(project.id, payload);
                              }
                            } catch {
                              // ignore malformed payload
                            }
                          }}
                        >
                          <button
                            onClick={() => handleSelectProject(project.id)}
                            className="flex-1 text-left flex items-center gap-3 min-w-0 basis-0 overflow-hidden"
                          >
                            <div className={cn(
                              "p-1 rounded-md flex-shrink-0 transition-colors",
                              selected
                                ? "bg-muted"
                                : "bg-muted group-hover:bg-accent-foreground/10"
                            )}>
                              <FolderIcon className="h-3.5 w-3.5" />
                            </div>
                            <div className="min-w-0 flex-1 overflow-hidden w-0">
                              <span
                                className={cn(
                                  "text-sm block truncate",
                                  selected ? "font-semibold" : "font-medium"
                                )}
                                title={project.name}
                              >
                                {project.name}
                              </span>
                              {project.description && (
                                <span
                                  className="text-xs text-muted-foreground block truncate"
                                  title={project.description}
                                >
                                  {project.description}
                                </span>
                              )}
                            </div>
                          </button>
                          <div
                            className="flex items-center opacity-0 transition-opacity group-hover:opacity-100"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              aria-label="Edit project"
                              onClick={(e) => {
                                e.stopPropagation();
                                onEdit(project);
                              }}
                              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent/50 transition-colors"
                            >
                              <PencilIcon className="h-3.5 w-3.5" />
                            </button>
                            <button
                              aria-label="Delete project"
                              onClick={(e) => {
                                e.stopPropagation();
                                onDelete(project);
                              }}
                              className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                            >
                              <TrashIcon className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* New Project - under project folders */}
              <button
                onClick={onCreateOpen}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-left text-muted-foreground hover:bg-accent hover:text-foreground group"
              >
                <div className="p-1 rounded-md flex-shrink-0 bg-muted group-hover:bg-accent-foreground/10 transition-colors">
                  <PlusIcon className="h-3.5 w-3.5" />
                </div>
                <span className="text-sm font-medium">New Project</span>
              </button>

              {/* Shared Projects */}
              <Separator className="my-4" />
              <div>
                <div className="flex items-center justify-between px-3 py-2 mb-2">
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Shared with me
                  </h3>
                  <Badge variant="secondary" className="text-xs">
                    {sharedProjects.length}
                  </Badge>
                </div>
                {sharedProjects.length === 0 ? (
                  <div className="px-3 py-8 text-center">
                    <div className="rounded-lg border border-dashed border-border bg-muted/30 p-6">
                      <UsersIcon className="h-10 w-10 mx-auto mb-3 text-muted-foreground/60" />
                      <p className="text-sm font-medium text-foreground mb-1">
                        No shared projects
                      </p>
                      <p className="text-xs text-muted-foreground mb-4">
                        Projects shared with you will appear here
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1 min-w-0">
                    {sharedProjects.map((project) => {
                      const selected = selectedId === project.id;
                      const isDragOver = dragOverProjectId === project.id;
                      return (
                        <button
                          key={project.id}
                          onClick={() => handleSelectProject(project.id)}
                          className={cn(
                            "w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-left group min-w-0 overflow-hidden border border-transparent",
                            selected
                              ? "bg-[hsl(var(--primary)/0.1)] text-primary"
                              : "hover:bg-accent",
                            isDragOver && !selected && "border-primary/50 bg-accent/60"
                          )}
                          onDragOver={(e) => {
                            if (!onItemDrop) return;
                            if (e.dataTransfer.types.includes('application/x-rcm-project-item')) {
                              e.preventDefault();
                              e.dataTransfer.dropEffect = 'copy';
                            }
                          }}
                          onDragEnter={(e) => {
                            if (!onItemDrop) return;
                            if (!e.dataTransfer.types.includes('application/x-rcm-project-item')) {
                              return;
                            }
                            setDragOverProjectId(project.id);
                            if (typeof document !== 'undefined') {
                              const label = document.getElementById('rcm-drag-preview-label');
                              if (label) {
                                label.textContent = `Add to "${project.name}"`;
                              }
                            }
                          }}
                          onDragLeave={(e) => {
                            if (!onItemDrop) return;
                            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                              setDragOverProjectId((prev) =>
                                prev === project.id ? null : prev
                              );
                            }
                          }}
                          onDrop={(e) => {
                            if (!onItemDrop) return;
                            if (!e.dataTransfer.types.includes('application/x-rcm-project-item')) {
                              return;
                            }
                            e.preventDefault();
                            setDragOverProjectId(null);
                            const raw = e.dataTransfer.getData('application/x-rcm-project-item');
                            if (!raw) return;
                            try {
                              const payload = JSON.parse(raw) as {
                                id: string;
                                kind: 'Document' | 'Chat' | 'PDF';
                              }[];
                              if (Array.isArray(payload) && payload.length > 0) {
                                onItemDrop(project.id, payload);
                              }
                            } catch {
                              // ignore malformed payload
                            }
                          }}
                        >
                          <div className={cn(
                            "p-1 rounded-md flex-shrink-0 transition-colors",
                            selected
                              ? "bg-muted"
                              : "bg-muted group-hover:bg-accent-foreground/10"
                          )}>
                            <UsersIcon className="h-3.5 w-3.5" />
                          </div>
                          <span
                            className={cn(
                              "text-sm flex-1 min-w-0 w-0 truncate block",
                              selected ? "font-semibold" : "font-medium"
                            )}
                            title={project.name}
                          >
                            {project.name}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </nav>
          </ScrollArea>
      </ShellSidebarSlot>

      {/* Document detail / right sidebar */}
      {rightSidebar != null && (
        <aside className="document-detail-sidebar-box flex-col min-w-0 hidden lg:flex">
          {rightSidebar}
        </aside>
      )}

      <div className="app-content-box flex flex-1 min-w-0 overflow-hidden flex-col">
        <ContentBreadcrumbs />
        <div className="flex-1 min-w-0 flex flex-col overflow-auto bg-card">
          <main className="flex-1 min-w-0">{children}</main>
        </div>
      </div>
      </div>
    </div>
  );
}
